import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useLabOrder } from "../../stores/labOrderStore";
import { SOIL_LABS, packagesFor } from "../../lib/soilLabs";
import {
  CORES,
  CORE_LINE_FT,
  DEPTH_IN,
  clampCoreLineFt,
  clampCores,
  clampDepthIn,
  normalizeUsPhone,
} from "../../lib/orderOptions";

export function OrderOptions() {
  const o = useLabOrder();
  const [more, setMore] = useState(false);
  // drafts for the numeric boxes so typing "1" on the way to "12" is not clamped mid-entry
  const [depthDraft, setDepthDraft] = useState<string | null>(null);
  const [coresDraft, setCoresDraft] = useState<string | null>(null);
  const [lineDraft, setLineDraft] = useState<string | null>(null);
  const pkgs = packagesFor(o.lab);
  const phoneBad =
    o.notifyPhone.trim() !== "" && !normalizeUsPhone(o.notifyPhone);

  const numBox = (
    label: string,
    value: number,
    draft: string | null,
    setDraft: (v: string | null) => void,
    commit: (v: number) => void,
    clamp: (v: unknown) => number,
    unit: string,
    range: { min: number; max: number },
    ariaLabel: string,
  ) => (
    <label className="flex flex-col gap-0.5">
      <span className="text-[11px] text-muted">{label}</span>
      <span className="flex items-center gap-1">
        <input
          type="number"
          min={range.min}
          max={range.max}
          step={1}
          value={draft ?? String(value)}
          aria-label={ariaLabel}
          onFocus={() => setDraft(String(value))}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft != null) commit(clamp(draft));
            setDraft(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          className="tnum w-16 rounded border border-line bg-canvas px-1.5 py-1 text-[12px] text-fg"
        />
        <span className="text-[11px] text-faint">
          {unit} · {range.min}–{range.max}
        </span>
      </span>
    </label>
  );

  return (
    <div
      className="flex flex-col gap-2 rounded border border-line bg-canvas/40 p-2.5"
      data-testid="order-options"
    >
      <div className="text-[11px] font-semibold text-fg">Order options</div>
      <div className="grid grid-cols-2 gap-2">
        {numBox(
          "Sampling depth",
          o.depthIn,
          depthDraft,
          setDepthDraft,
          (v) => o.setOptions({ depthIn: v }),
          clampDepthIn,
          "in",
          DEPTH_IN,
          "Sampling depth (inches)",
        )}
        <label className="flex flex-col gap-0.5">
          <span className="text-[11px] text-muted">Lab</span>
          <select
            value={o.lab}
            aria-label="Lab"
            onChange={(e) => o.setOptions({ lab: e.target.value })}
            className="rounded border border-line bg-canvas px-1.5 py-1 text-[12px] text-fg"
          >
            {SOIL_LABS.map((l) => (
              <option key={l.key} value={l.key}>
                {l.shortName}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex flex-col gap-0.5">
        <span className="text-[11px] text-muted">Lab package</span>
        {pkgs.length ? (
          <select
            value={o.labPackage}
            aria-label="Lab package"
            onChange={(e) => o.setOptions({ labPackage: e.target.value })}
            className="rounded border border-line bg-canvas px-1.5 py-1 text-[12px] text-fg"
          >
            {pkgs.map((p) => (
              <option key={p.code} value={p.code}>
                {p.code} — {p.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="rounded border border-dashed border-line px-1.5 py-1 text-[11px] text-faint">
            This lab's package menu isn't loaded yet — the order will name the
            lab only.
          </span>
        )}
        {pkgs.find((p) => p.code === o.labPackage)?.analytes.length ? (
          <span className="text-[10px] leading-snug text-faint">
            {pkgs.find((p) => p.code === o.labPackage)!.analytes.join(", ")}
          </span>
        ) : null}
      </label>
      <div>
        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          aria-expanded={more}
          className="flex items-center gap-1 text-[11px] font-medium text-muted hover:text-fg"
        >
          {more ? <ChevronDown size={11} /> : <ChevronRight size={11} />}{" "}
          Additional config options
          <span className="font-normal text-faint">
            · {o.coresPerSample} cores/sample · {o.coreLineFt} ft line
          </span>
        </button>
        {more && (
          <div className="mt-1.5 grid grid-cols-2 gap-2 border-l border-line pl-2">
            {numBox(
              "Cores per sample",
              o.coresPerSample,
              coresDraft,
              setCoresDraft,
              (v) => o.setOptions({ coresPerSample: v }),
              clampCores,
              "cores",
              CORES,
              "Cores per sample",
            )}
            {numBox(
              "Core line length",
              o.coreLineFt,
              lineDraft,
              setLineDraft,
              (v) => o.setOptions({ coreLineFt: v }),
              clampCoreLineFt,
              "ft",
              CORE_LINE_FT,
              "Core line length (feet)",
            )}
          </div>
        )}
      </div>
      <label className="flex flex-col gap-0.5">
        <span className="text-[11px] text-muted">
          Sampling notifications phone number{" "}
          <span className="text-faint">(US, optional)</span>
        </span>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(555) 123-4567"
          value={o.notifyPhone}
          aria-label="Sampling notifications phone number"
          aria-invalid={phoneBad || undefined}
          onChange={(e) => o.setOptions({ notifyPhone: e.target.value })}
          className={`rounded border bg-canvas px-1.5 py-1 text-[12px] text-fg ${phoneBad ? "border-red-500" : "border-line"}`}
        />
        {phoneBad && (
          <span className="text-[10px] text-red-400">
            Enter a valid US phone number (10 digits), or leave it blank.
          </span>
        )}
      </label>
    </div>
  );
}
